import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import About from "./components/About";
import Skills from "./components/Skills";
import ReferIcon from "@assets/svg/ReferIcon";
import TrackingIcon from "@assets/svg/TrackingIcon";
import ShareIconOutline from "@assets/svg/ShareIconOutline";
import LikeIconLeft from "@assets/svg/LikeIconLeft";
import ContactDetails from "./components/ContactDetails";
import { useSelector } from "react-redux";
import {
  getCandidateDetails,
  getSkills,
  updateCandidate,
} from "@endpoints/candidate.service";
import { useDispatch } from "react-redux";
import { setUserData } from "@store/actions/auth.action";
import { candidateStatus, userTypes } from "@utils/constants";
import { useSnackbar } from "notistack";
import Education from "./components/Education";
import { getExperienceList } from "@endpoints/experience.service";
import { getCertificateOrEducation } from "@endpoints/education.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { FormProvider, useForm } from "react-hook-form";
import "cropperjs/dist/cropper.css";

import {
  BriefcaseBusiness,
  ChevronDown,
  MapPin,
  PencilLine,
} from "lucide-react";

import dayjs from "dayjs";
import Experience2 from "./components/experience/experience2";
import { ProfileProgressContext } from "@pages/Layout/provider/CreateProfileProvider";
import SetupDialog from "./components/SetupDialog";
import EditProfileModal from "./components/EditProfileModal";

const availabilityStatus = Object.entries(candidateStatus).map(
  ([_, value]) => ({
    title: value,
    id: value,
  }),
);

const RenderBody = forwardRef(
  (
    {
      activeTab,
      isEditing,
      setEditingTab,
      setIsEditing,
      handleSave,
      handleEditClose,
      candidateId,
    },
    ref,
  ) => {
    switch (activeTab) {
      case "about":
        return (
          <About
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            setIsEditing={setIsEditing}
            handleSave={handleSave}
          />
        );
      case "experience":
        return <Experience2 />;
      case "skills":
        return (
          <Skills
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            candidateId={candidateId}
          />
        );
      case "education":
        return (
          <Education
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
          />
        );
      case "contact":
        return (
          <ContactDetails
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            handleSave={handleSave}
          />
        );
      default:
        return <div />;
    }
  },
);
RenderBody.displayName = "RenderBody";

const CreateProfileNew = () => {
  const { tabs, activeTab, setActiveTab } = useContext(ProfileProgressContext);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef();
  const hasCheckedRequirementRef = useRef(false);

  const { user } = useSelector((state) => state.auth);

  // Local state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingWorkStatus, setIsUpdatingWorkStatus] = useState(false);
  const [showRequiredInfoModal, setShowRequiredInfoModal] = useState(false);
  const [requirementsDataLoaded, setRequirementsDataLoaded] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      jobTitle: user?.jobTitle,
      status: availabilityStatus?.find((x) => x.id === user?.status)?.id ?? "",
      location: user?.location,
      image: undefined,
      openProfileModel: false,
    },
  });

  const activeAvailabilityStatus = useMemo(
    () => availabilityStatus?.find((x) => x.id === user?.status)?.title,
    [user],
  );

  const handleTabClick = (value) => {
    setIsEditing(false);
    setEditingTab(undefined);
    setActiveTab(value);
  };

  const handleSave = async (formData, enable) => {
    console.log("this is formData", formData);
    if (activeTab === 0 || activeTab === 4 || enable) {
      try {
        setIsLoading(true);
        const { success, data } = await updateCandidate(formData);
        if (success) {
          dispatch(setUserData(data));
          setIsEditing(false);
          setEditingTab(activeTab);
        }
      } catch (error) {
        enqueueSnackbar(error, {
          variant: "error",
        });
      } finally {
        setIsEditing(false);
        setIsLoading(false);
      }
    }
  };

  const handleUpdateWorkStatus = async (status) => {
    try {
      setIsUpdatingWorkStatus(true);
      const { success, data } = await updateCandidate({ status });
      if (success) {
        dispatch(setUserData(data));
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setIsUpdatingWorkStatus(false);
    }
  };

  const fetchCandidateDetails = useCallback(async () => {
    try {
      const { success, data } = await getCandidateDetails();
      if (success) {
        const { _id, ...rest } = data;
        dispatch(setUserData({...rest,candidateId:_id}));  
      }
    } catch (error) {
      throw error?.message;
    }
  }, []);

  const fetchExperienceList = useCallback(async () => {
    try {
      const { success, data } = await getExperienceList();
      if (success) {
        dispatch(setUserData({ experiences: data }));
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchUserSkills = useCallback(async (candidateId = user._id) => {
    try {
      const { success, data } = await getSkills(candidateId);

      if (success) {
        dispatch(setUserData({ skills: data }));
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchEducationAndCertificates = useCallback(async () => {
    try {
      const { success, data } = await getCertificateOrEducation();
      if (success) {
        dispatch(
          setUserData({
            educations: data?.educations ?? [],
            certificates: data?.certificates ?? [],
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      await Promise.allSettled([
        fetchCandidateDetails(),
        fetchExperienceList(),
        fetchUserSkills(),
        fetchEducationAndCertificates(),
      ]);
      setRequirementsDataLoaded(true);
    };
    loadAll();
  }, []);

  useEffect(() => {
    if (!requirementsDataLoaded || hasCheckedRequirementRef.current) return;
    const hasExperience = Array.isArray(user?.experiences)
      ? user.experiences.length > 0
      : false;
    const hasEducation = Array.isArray(user?.educations)
      ? user.educations.length > 0
      : false;
    const hasCertificate = Array.isArray(user?.certificates)
      ? user.certificates.length > 0
      : false;
    if (!hasExperience && !hasEducation && !hasCertificate) {
      setShowRequiredInfoModal(true);
    }
    hasCheckedRequirementRef.current = true;
  }, [
    requirementsDataLoaded,
    user?.experience,
    user?.educations,
    user?.certificates,
  ]);

  const closeRequiredInfoModal = () => {
    setShowRequiredInfoModal(false);
  };

  const handleNavigateFromRequiredInfo = (tabValue) => {
    setIsEditing(false);
    setEditingTab(undefined);
    setActiveTab(tabValue);
    closeRequiredInfoModal();
  };

  const baseUrl =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://comtracked";
  const profileLink = user?.userName
    ? `${baseUrl}/profile/${user.userName}`
    : "";

  const handleCopyProfileLink = async () => {
    if (!profileLink) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileLink);
      } else {
        const tempInput = document.createElement("input");
        tempInput.value = profileLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        tempInput.remove();
      }
      enqueueSnackbar("Profile link copied.", { variant: "success" });
    } catch (error) {
      console.error("Error copying profile link:", error);
      enqueueSnackbar("Unable to copy profile link.", { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      {/* Tabs container */}
      <div
        role="tablist"
        className="flex items-center justify-evenly h-[51px] border-b-2 gap-5 bg-white rounded-t-md overflow-hidden"
      >
        {tabs?.map((item) => {
          const isActive = activeTab === item?.value;
          return (
            <button
              key={item?.value}
              role="tab"
              aria-selected={isActive}
              aria-label={item?.label}
              onClick={() => handleTabClick(item?.value)}
              className={`flex flex-col flex-1 h-full items-center justify-center overflow-hidden transition-all duration-300 ease-out ${isActive ? "text-slate-700" : "text-[#525252] hover:bg-[#f5f5f5]"}`}
            >
              <div className="lg:hidden">{item?.icon}</div>
              <div className="font-montserrat font-medium text-xs hidden lg:flex items-center flex-1 justify-center">
                {item?.label}
              </div>
              <div
                className={`h-1 w-full rounded-full ${isActive ? "bg-slate-700" : "bg-transparent"}`}
              ></div>
            </button>
          );
        })}
      </div>
      {/* Main Body */}
      <div className="flex flex-col flex-1 rounded-b-md bg-white min-h-0 pb-6">
        <div className="flex flex-col flex-1 overflow-y-scroll no-scrollbar min-h-0 px-6">
          {/* Profile info section */}
          <div className="flex relative pl-5 my-6">
            <div className="flex gap-7 flex-1">
              {/* Profile Image */}
              <div className="flex items-center justify-center relative h-[124px] w-[124px] rounded-full bg-gray-100 border">
                {/* Edit Icon */}
                <button
                  onClick={() => setShowEditProfile(true)}
                  className="flex items-center justify-center bg-gray-200 h-6 w-6 rounded-full absolute right-1.5 top-1.5 hover:bg-gray-300 hover:scale-105 z-50"
                >
                  <PencilLine size={16} />
                </button>
                {user?.profilePic ? (
                  <img
                    src={user?.profilePic}
                    className="rounded-full h-full w-full object-fill z-10 max-md:h-36 max-md:w-36"
                  />
                ) : (
                  <span className="font-montserrat text-light-black font-semibold text-5xl uppercase">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 flex-1 mt-1.5">
                <span className="font-inter font-bold uppercase text-4xl text-slate-700">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="font-medium text-[#525252] text-xl">
                  {user?.jobTitle || "N/A"}
                </span>
                <div className="flex items-center gap-10 mt-2.5">
                  <div className="flex items-center gap-1">
                    <BriefcaseBusiness size={15} />
                    <span className="text-[#525252] font-medium text-xs">
                      Technology
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin size={15} />
                    <span className="text-[#525252] font-medium text-xs">
                      {user?.location || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Status container */}
            <div className="flex flex-col justify-between">
              <div className="flex items-center text-xs font-medium text-[#737373] gap-2">
                Status:
                <DropdownMenu>
                  <DropdownMenuTrigger className="group flex gap-1 items-center px-2 py-1.5 rounded-md text-[#007A55] bg-[#D4FFEB] transition-colors duration-200 hover:bg-[#BFF5DD] data-[state=open]:bg-[#BFF5DD]">
                    {activeAvailabilityStatus}
                    {isUpdatingWorkStatus ? (
                      <LoadingAnimation color={"black"} className="w-4 h-4" />
                    ) : (
                      <ChevronDown
                        size={16}
                        className="transition-transform duration-300 group-data-[state=open]:rotate-180"
                      />
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white" align="start">
                    {availabilityStatus?.map((item) => {
                      return (
                        <DropdownMenuItem
                          className="text-black outline-none font-medium text-xs cursor-pointer hover:bg-gray-100 hover:text-[#007A55]"
                          key={item?.id}
                          value={item?.id}
                          onSelect={() => handleUpdateWorkStatus(item?.id)}
                        >
                          {item?.title}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex flex-row-reverse items-center mr-5">
                <div
                  onClick={() => {
                    if (user?.userType === userTypes.CANDIDATE) {
                      handleCopyProfileLink();
                    }
                  }}
                  className="flex border border-[#DDDDDD] h-[40px] w-[56px] rounded-r-md justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95"
                >
                  <ShareIconOutline className={"w-10 h-10"} />
                </div>

                <div className="flex border-y border-l border-[#DDDDDD] h-[40px] w-[56px] justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <TrackingIcon className={"w-10 h-10"} fill={"#2E2E2E"} />
                </div>

                <div className="flex border-y border-[#DDDDDD] h-[40px] w-[56px] justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <ReferIcon className="w-7 h-7" />
                </div>

                <div className="flex border border-[#DDDDDD] h-[40px] w-[56px] rounded-l-md justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <LikeIconLeft className={"w-7 h-7 mt-0.5"} fill={"#2E2E2E"} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col w-full min-h-0 border-t">
            <span className="italic text-xs text-[#BABABA] ml-auto mr-1 my-3">
              Last updated at: {dayjs(user?.updatedA).format("MMMM YYYY")}
            </span>
            <FormProvider {...formMethods}>
              <RenderBody
                ref={formRef}
                activeTab={activeTab}
                isEditing={isEditing}
                editingTab={editingTab}
                handleSave={handleSave}
                setIsEditing={setIsEditing}
                handleEditClose={() => setIsEditing(false)}
                candidateId={user?._id}
              />
            </FormProvider>
          </div>
        </div>

        {/* Edit Profile Modal */}
        <EditProfileModal
          showModal={showEditProfile}
          setShowModal={setShowEditProfile}
        />

        {/* Setup Dialog */}
        <SetupDialog
          handleNavigateFromRequiredInfo={handleNavigateFromRequiredInfo}
          showRequiredInfoModal={showRequiredInfoModal}
          closeRequiredInfoModal={closeRequiredInfoModal}
        />
      </div>
    </div>
  );
};

export default CreateProfileNew;
